/**
 * CameraOffset: shifts the camera's view so the model appears centered
 * in the visible area (accounting for UI overlays like the parameter panel).
 *
 * Uses Three.js camera.setViewOffset() to shift the rendered viewport
 * without affecting orbit controls, bounds fitting, or model position.
 * This is a pure visual shift — the orbit target stays at the model center.
 *
 * @param offsetX — horizontal pixel offset (positive = shift model to the left,
 *   i.e. the visible center moves left). For a right-side panel of width W,
 *   pass W/2 to center the model in the remaining space.
 */

import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';

interface CameraOffsetProps {
  /** Horizontal offset in pixels (positive shifts the visual center to the left) */
  offsetX?: number;
}

export function CameraOffset({ offsetX = 0 }: CameraOffsetProps) {
  const { camera, size } = useThree();

  useEffect(() => {
    if (offsetX === 0) {
      // Clear any existing view offset
      camera.clearViewOffset();
    } else {
      // setViewOffset(fullWidth, fullHeight, offsetX, offsetY, width, height)
      // We keep the same resolution but shift the view window
      camera.setViewOffset(
        size.width,
        size.height,
        offsetX, // shift right in texture space = model appears shifted left
        0,
        size.width,
        size.height,
      );
    }
    camera.updateProjectionMatrix();

    return () => {
      camera.clearViewOffset();
      camera.updateProjectionMatrix();
    };
  }, [camera, offsetX, size.width, size.height]);

  return null;
}
